export interface SitePhoto {
  src: string
  alt: string
  caption: string
  /** CSS object-position, so faces stay in frame when cropped. */
  position?: string
  /** Extra tone correction for this photo, e.g. lifting a dark interior. */
  adjust?: string
}

export const PHOTOS = {
  teamOutdoor: {
    src: 'https://cdn.magicpatterns.com/uploads/2YEBK2F94RFRfHaT1VFQUB/IMG_3852.jpg',
    alt: 'Eight members of the Cavalla team standing together on a paved garden path, smiling',
    caption: 'The Cavalla team.',
    position: '50% 40%',
  },
  govTraining: {
    src: 'https://cdn.magicpatterns.com/uploads/rDfLnx21dpCmQehVCCS1rq/Gov_Line_Manager_Training_.jpg',
    alt: 'A facilitator presenting to government line managers seated around a boardroom table with laptops',
    caption: 'Line manager training with a government team.',
    position: '55% 50%',
  },
  kigaliMeeting: {
    src: 'https://cdn.magicpatterns.com/uploads/hx4awweQa7hX9tZxop6Qwx/Team_meeting_in_Kigali,_Rwanda.jpg',
    alt: 'Two colleagues at a meeting room table with laptops, one standing and one typing',
    caption: 'A team meeting in Kigali, Rwanda.',
    position: '50% 62%',
  },
  teamSession: {
    src: 'https://cdn.magicpatterns.com/uploads/353dbDcNJDcXxE68ojyjy7/Team_Session.jpg',
    alt: 'Two colleagues sitting together at a laptop in a meeting room, smiling at the camera',
    caption: 'A working session in the Kigali office.',
    position: '50% 66%',
  },
  liberia: {
    src: 'https://cdn.magicpatterns.com/uploads/crP9TtYx4V9FM4bqiD4CWY/Jonathan_and_Ex_Liberia_President.jpg',
    alt: 'Jonathan standing beside a former President of Liberia in a hotel suite',
    caption: 'Jonathan with a former President of Liberia.',
    position: '50% 28%',
  },
  officeAnalysts: {
    src: 'https://cdn.magicpatterns.com/uploads/tBqxX8rNpjB2TRnfh2Bv9k/cavalla_1.jpg',
    alt: 'Analysts working at desktop computers in the Cavalla office',
    caption: 'Analysts at work in the Cavalla office.',
    position: '45% 40%',
  },
  officeWide: {
    src: 'https://cdn.magicpatterns.com/uploads/nvQLFXt4HgP6eTKwJPDpoP/Cavalla.jpg',
    alt: 'The Cavalla office, with analysts working at computers near a window and a whiteboard',
    caption: 'The Cavalla analysis room.',
    position: '60% 45%',
    adjust: 'brightness(1.28) contrast(1.06)',
  },
  ethiopiaPortrait: {
    src: 'https://cdn.magicpatterns.com/uploads/9rAMNyaHNwG8JrJSdiXbJ1/Mrs._Amanwit_Shita,_a_student_and_a_WASH_club_lead_at_Bisike_School_at_Adama_Woreda,_where_CARE_implemented_the_DIP-MHH_project._Oct_2024_IMG_5643.jpg',
    alt: 'A smiling student in a green high visibility vest in a school yard',
    caption: 'A student WASH club lead at Bisike School, Adama Woreda, Ethiopia.',
    position: '58% 35%',
  },
  ethiopiaKit: {
    src: 'https://cdn.magicpatterns.com/uploads/5UdvnNMcSBWGFUArMxnRq8/Mrs._Amanwit_Shita,_a_student_and_a_WASH_club_lead_at_Bisike_School_at_Adama_Woreda,_where_CARE_implemented_the_DIP-MHH_project._Oct_2024_IMG_5686.jpg',
    alt: 'A student in a high visibility vest holding a sealed hygiene kit under a tree',
    caption: 'A WASH club lead with a hygiene kit, Bisike School, Ethiopia.',
    position: '55% 35%',
  },
  planIndonesia: {
    src: 'https://cdn.magicpatterns.com/uploads/mB5FaqxCxEkhc6uPGuqW2x/9e21926b-26d8-4a8c-95c7-36d68601a95d.jpg',
    alt: 'Three people standing beside a reception desk at Plan International Indonesia',
    caption: 'Meeting Yayasan Plan International Indonesia.',
    position: '65% 40%',
  },
  friendsInternational: {
    src: 'https://cdn.magicpatterns.com/uploads/g1DeeZjcrf6aBsgqPZMNgk/603d1827-5a23-41c5-b758-380d663d672d_(1).jpg',
    alt: 'Three women smiling beneath a Friends-International world map',
    caption: 'With Yayasan Teman Baik, part of the Friends-International network, Indonesia.',
    position: '50% 65%',
  },
  workshopWide: {
    src: 'https://cdn.magicpatterns.com/uploads/my76T29ZhGRNfg6SfCYLeP/WhatsApp_Image_2025-07-01_at_09.19.25_468f21e1_(1).jpg',
    alt: 'Participants around a U shaped table in a workshop, one standing to guide a small group exercise',
    caption: 'A working session on clarifying programme indicators.',
    position: '60% 50%',
  },
  workshopTable: {
    src: 'https://cdn.magicpatterns.com/uploads/mnJWapwnBdFS7uDvR7FgKN/WhatsApp_Image_2025-07-01_at_09.19.25_435217e9.jpg',
    alt: 'A small group leaning over printed worksheets at a workshop table',
    caption: 'Small group work during an indicators workshop.',
    position: '50% 62%',
  },
  workshopRoom: {
    src: 'https://cdn.magicpatterns.com/uploads/pDU1J1pCu6MjqM8B7i5Kke/WhatsApp_Image_2025-07-01_at_09.19.26_617934f1_(1).jpg',
    alt: 'A workshop room with participants working in groups in front of a projected slide',
    caption: 'Workshop participants working through an indicators exercise.',
    position: '55% 50%',
  },
} satisfies Record<string, SitePhoto>

/** Sharpening and gentle tone lift applied to every site photograph. */
export function photoFilter(photo: Pick<SitePhoto, 'adjust'>): string {
  return `url(#cavalla-sharpen) contrast(1.05) saturate(1.06)${photo.adjust ? ` ${photo.adjust}` : ''}`
}
